import React, { useState } from "react";
import { MdExpandMore, MdLockOpen, MdShield } from "react-icons/md";
import {
  FieldNode,
  getParameters,
  getRequestBody,
  getResponses,
  HttpMethod,
  OpenApiDoc,
  Operation,
  rotuloPermissoes,
} from "../openapi";

const METODO_CLS: Record<HttpMethod, string> = {
  get: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  post: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  put: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  patch: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  delete: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  head: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  options: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
};

const FieldRows: React.FC<{ fields: FieldNode[]; nivel?: number }> = ({ fields, nivel = 0 }) => (
  <>
    {fields.map((f) => (
      <React.Fragment key={`${nivel}-${f.name}`}>
        <tr className="border-t border-gray-100 align-top dark:border-white/10">
          <td className="py-1.5 pr-3 font-mono text-xs text-navy-700 dark:text-white" style={{ paddingLeft: nivel * 16 }}>
            {f.name}
            {f.required && <span className="ml-1 text-red-600 dark:text-red-400" title="Obrigatório">*</span>}
          </td>
          <td className="py-1.5 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
            {f.type}
            {f.ciclo && <span className="ml-1 italic">(recursivo)</span>}
            {f.truncado && <span className="ml-1 italic">(mais campos…)</span>}
          </td>
          <td className="py-1.5 text-xs text-gray-700 dark:text-gray-300">
            {f.description}
            {f.enumValues && <span className="block text-gray-600 dark:text-gray-400">Valores: {f.enumValues.join(" | ")}</span>}
          </td>
        </tr>
        {f.children.length > 0 && <FieldRows fields={f.children} nivel={nivel + 1} />}
      </React.Fragment>
    ))}
  </>
);

const FieldTable: React.FC<{ fields: FieldNode[] }> = ({ fields }) => (
  <table className="mt-1 w-full text-left">
    <thead>
      <tr className="text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
        <th className="pb-1 pr-3">Campo</th>
        <th className="pb-1 pr-3">Tipo</th>
        <th className="pb-1">Descrição</th>
      </tr>
    </thead>
    <tbody>
      <FieldRows fields={fields} />
    </tbody>
  </table>
);

const Secao: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div className="mt-3">
    <p className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">{titulo}</p>
    {children}
  </div>
);

interface Props {
  doc: OpenApiDoc;
  op: Operation;
}

/** Uma operação da API: cabeçalho sempre visível, detalhes (parâmetros, corpo, respostas) ao expandir. */
const OperationCard: React.FC<Props> = ({ doc, op }) => {
  const [aberta, setAberta] = useState(false);
  const painelId = `op-${op.key.replace(/[^a-zA-Z0-9]/g, "-")}`;

  // Só calcula o detalhe quando abre: módulos grandes têm centenas de operações.
  const detalhe = aberta ? { params: getParameters(doc, op), corpo: getRequestBody(doc, op), respostas: getResponses(doc, op) } : null;

  return (
    <div className="rounded-xl border border-gray-100 bg-white dark:border-white/10 dark:bg-navy-800">
      <button
        type="button"
        onClick={() => setAberta((v) => !v)}
        aria-expanded={aberta}
        aria-controls={painelId}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 dark:hover:bg-navy-700"
      >
        <span className={`w-16 shrink-0 rounded-md px-2 py-0.5 text-center text-[11px] font-bold uppercase ${METODO_CLS[op.method]}`}>
          {op.method}
        </span>
        <code className="min-w-0 break-all font-mono text-xs text-navy-700 dark:text-white">{op.path}</code>
        <span className="min-w-0 flex-1 text-xs text-gray-700 dark:text-gray-300">{op.summary}</span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            op.anonimo
              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              : "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300"
          }`}
        >
          {op.anonimo ? <MdLockOpen className="h-3.5 w-3.5" aria-hidden="true" /> : <MdShield className="h-3.5 w-3.5" aria-hidden="true" />}
          {rotuloPermissoes(op)}
        </span>
        {op.restricoes.length > 0 && (
          <ul aria-label="Restrições" className="m-0 flex basis-full list-none flex-wrap gap-1.5 p-0">
            {op.restricoes.map((r) => (
              <li key={r} className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                {r}
              </li>
            ))}
          </ul>
        )}
        <MdExpandMore className={`h-5 w-5 shrink-0 text-gray-600 transition-transform dark:text-gray-400 ${aberta ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {detalhe && (
        <div id={painelId} className="border-t border-gray-100 px-3 pb-3 dark:border-white/10">
          {op.description && <p className="mt-3 whitespace-pre-line text-xs text-gray-700 dark:text-gray-300">{op.description}</p>}

          {detalhe.params.length > 0 && (
            <Secao titulo="Parâmetros">
              <table className="mt-1 w-full text-left">
                <tbody>
                  {detalhe.params.map((p) => (
                    <tr key={`${p.in}-${p.name}`} className="border-t border-gray-100 align-top dark:border-white/10">
                      <td className="py-1.5 pr-3 font-mono text-xs text-navy-700 dark:text-white">
                        {p.name}
                        {p.required && <span className="ml-1 text-red-600 dark:text-red-400" title="Obrigatório">*</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-xs text-gray-700 dark:text-gray-300">{p.in}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">{p.type}</td>
                      <td className="py-1.5 text-xs text-gray-700 dark:text-gray-300">{p.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Secao>
          )}

          {detalhe.corpo && (
            <Secao titulo={`Corpo da requisição (${detalhe.corpo.contentType})`}>
              {detalhe.corpo.fields.length > 0 ? (
                <FieldTable fields={detalhe.corpo.fields} />
              ) : (
                <p className="font-mono text-xs text-gray-700 dark:text-gray-300">{detalhe.corpo.type}</p>
              )}
            </Secao>
          )}

          {detalhe.respostas.length > 0 && (
            <Secao titulo="Respostas">
              {detalhe.respostas.map((r) => (
                <div key={r.status} className="mt-1.5">
                  <p className="text-xs text-navy-700 dark:text-white">
                    <span className="font-mono font-bold">{r.status}</span>
                    {r.description ? ` — ${r.description}` : ""}
                    {r.body && <span className="ml-2 font-mono text-gray-700 dark:text-gray-300">{r.body.type}</span>}
                  </p>
                  {r.body && r.body.fields.length > 0 && <FieldTable fields={r.body.fields} />}
                </div>
              ))}
            </Secao>
          )}

          {detalhe.params.length === 0 && !detalhe.corpo && detalhe.respostas.length === 0 && !op.description && (
            <p className="mt-3 text-xs text-gray-700 dark:text-gray-300">Esta operação não tem parâmetros, corpo ou respostas documentados.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default OperationCard;
